import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { useLazyGetNotificationsAllQuery } from '@/api/queries/notifications';
import { ResourceTitle } from '@/components/ResourceTitle/ResourceTitle';
import { useAppSelector } from '@/redux/store';

import NotificationListItem from './ui/NotificationListItem';

import styles from './styles.module.scss';

const Notifications: FC = () => {
  const { t } = useTranslation();
  const notifications = useAppSelector((state) => state.user.notifications);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const observerCallbackRef = useRef<() => void>();

  const [fetch, { isFetching, data }] = useLazyGetNotificationsAllQuery();

  useEffect(() => {
    if (!notifications.length) {
      fetch({ skip: 0 });
    }
  }, []);

  useEffect(() => {
    observerCallbackRef.current = (): void => {
      console.debug(isFetching, notifications.length, data);

      if (!isFetching && notifications.length < (data?.total ?? 0)) {
        fetch({ skip: notifications.length });
      }
    };
  }, [data?.total, notifications.length, isFetching]);

  useEffect(() => {
    const sentinel = sentinelRef.current;

    if (!sentinel) {
      return;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (entry.isIntersecting) {
          observerCallbackRef.current?.();
        }
      },
      { threshold: 0.1 },
    );

    observerRef.current.observe(sentinel);

    return (): void => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return (
    <main className={styles.wrapper}>
      <Helmet>
        <title>Notifications</title>
      </Helmet>
      <ResourceTitle variant="plain">{t('Notifications')}</ResourceTitle>
      <ul className="mt-6 flex flex-col gap-2">
        {notifications.map((notification) => (
          <NotificationListItem {...notification} key={notification.id} />
        ))}
      </ul>
      <div ref={sentinelRef} className="h-1" />
    </main>
  );
};

export default Notifications;
